/* ============================================================
   Onboarding connect step → social OAuth → back to onboarding.

   OnboardingActivation (phase connect_accounts) sets
   ONBOARDING_SOCIAL_RETURN_KEY and starts the connect with returnPath
   /app/settings/integrations. The auth-worker sends the browser back there
   with the OAuth result (?provider=…&connected=… or ?error=…), and the app
   resumes onboarding at /onboarding?continue=activation&….

   Two places act on that return, and both must build the same URL:
     * MainApp's resume effect (App.jsx) — every account that gets past the
       /app guards;
     * RequireActiveBilling's wall branch (App.jsx, P1 of GH-1133 / #1150) —
       a card-less ('new') account, which the card gate used to stop on the
       wall mid-onboarding, so it never saw the "Profile ready" screen.
   ============================================================ */

export const ONBOARDING_SOCIAL_RETURN_KEY = 'dathent_onboarding_social_oauth_return';

// OAuth result params forwarded to the onboarding resume.
// 'limit'/'plan' ride along on auth-worker's social_accounts_limit_reached
// redirect (302 ?error=social_accounts_limit_reached&limit=<n>&plan=<key>)
// so OnboardingActivation can word its capacity banner with a real number
// instead of guessing; 'keptBrand' flags a cross-org same-account reconnect
// that was kept on its existing brand; 'limitReached=1' marks a PARTIAL
// Meta multi-account connect (some accounts landed, the rest were capped
// mid-batch) — same param Settings.jsx already reads for its own banner.
export const ONBOARDING_SOCIAL_RETURN_PARAMS = Object.freeze([
  'provider', 'platform', 'connected', 'accounts', 'error', 'pages',
  'instagramBusinessAccounts', 'limit', 'plan', 'keptBrand', 'limitReached',
]);

/** window.localStorage, or null where the browser blocks it. Never throws. */
export function browserStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function readFlag(storage) {
  try {
    return Boolean(storage?.getItem(ONBOARDING_SOCIAL_RETURN_KEY));
  } catch {
    return false;
  }
}

/**
 * Is this location the onboarding connect step's OAuth return?
 * Same test as MainApp's resume branch: an /app/settings/integrations path,
 * an OAuth result param, the onboarding flag set — and no ?overage=, which
 * always goes to Settings (TASK-329 guard contract). Fails closed (false)
 * on a missing location or unreadable storage.
 */
export function isOnboardingSocialReturn(location, storage) {
  if (!location) return false;
  const normalizedPath = String(location.pathname || '').replace(/^\/app(?=\/|$)/, '') || '/';
  if (!normalizedPath.startsWith('/settings/integrations')) return false;
  const params = new URLSearchParams(location.search || '');
  if (params.has('overage')) return false;
  if (!(params.has('provider') || params.has('connected') || params.has('error'))) return false;
  return readFlag(storage);
}

/** The onboarding resume URL for an OAuth return's query string. */
export function onboardingSocialResumePath(search) {
  const params = new URLSearchParams(search || '');
  const returnParams = new URLSearchParams({ continue: 'activation' });
  ONBOARDING_SOCIAL_RETURN_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) returnParams.set(key, value);
  });
  return `/onboarding?${returnParams.toString()}`;
}

/** Consume the one-shot flag. Never throws. */
export function clearOnboardingSocialReturn(storage) {
  try {
    storage?.removeItem(ONBOARDING_SOCIAL_RETURN_KEY);
  } catch {
    /* blocked storage: the resume still happens */
  }
}
